import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { array, mixed, number, object, string } from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
// @mui
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  DialogActions,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import { Iconify, Label, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableHeadCustom,
  TableMoreMenu,
  TableNoData,
} from '@/components/table';
import FormProvider from '@/components/hook-form/FormProvider';
import RHFTextField from '@/components/hook-form/RHFTextField';
import { SectionTitle } from '@/features/users/shared/components';
// api
import {
  createClientAdminReminder,
  deleteClientAdminReminder,
  getActiveDashboardUsers,
  getClientAdminReminders,
  updateClientAdminReminder,
} from '@/features/users/shared/api';
// hooks + utils
import { use3DotMenu } from '@/hooks/use3DotMenu';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useToggle from '@/hooks/useToggle';
import {
  normalizeTaskAssigneeIds,
  TASK_PRIORITY_OPTIONS,
} from '@/features/tasks/utils';
// types
import type { TaskPriority } from '@/features/tasks/types';
import type {
  ClientAdminReminder,
  ClientAdminReminderDto,
  User,
} from '@/features/users/shared/types';

type DashboardUserOption = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  profile_image?: string | null;
  job_title?: string | null;
  display_name?: string | null;
};

type DashboardUserSource = {
  id?: number | string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  profile_image?: string | null;
  job_title?: string | null;
  display_name?: string | null;
};

type ClientAdminReminderFormValues = {
  title: string;
  note: string;
  days_before: number | string;
  task_priority: TaskPriority;
  assigned_dashboard_user_ids: number[];
};

type UpdateReminderVariables = {
  ruleId: number;
  payload: ClientAdminReminderDto;
};

const CLIENT_ADMIN_REMINDERS_TABLE_HEAD = [
  { id: 'title', label: 'Title', align: 'left', minWidth: 180 },
  { id: 'days_before', label: 'Days Before', align: 'left', minWidth: 120 },
  { id: 'priority', label: 'Priority', align: 'left', minWidth: 120 },
  { id: 'assignees', label: 'Assignees', align: 'left', minWidth: 220 },
  { id: 'note', label: 'Note', align: 'left', minWidth: 280 },
  { id: '3dotMenu', label: '', align: 'right', width: 64 },
];

const TASK_PRIORITY_VALUES: TaskPriority[] = [
  'LOW',
  'NORMAL',
  'HIGH',
  'URGENT',
];

const DEFAULT_REMINDER_FORM_VALUES: ClientAdminReminderFormValues = {
  title: '',
  note: '',
  days_before: 1,
  task_priority: 'HIGH',
  assigned_dashboard_user_ids: [],
};

const ADMIN_REMINDER_FORM_SCHEMA = object().shape({
  title: string()
    .trim()
    .required('Title is required')
    .max(255, 'Title must be 255 characters or fewer'),
  note: string().trim().max(1000, 'Note must be 1000 characters or fewer'),
  days_before: number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .typeError('Days before is required')
    .required('Days before is required')
    .integer('Days before must be a whole number')
    .min(0, 'Days before must be at least 0')
    .max(365, 'Days before must be 365 or fewer'),
  task_priority: mixed<TaskPriority>()
    .oneOf(TASK_PRIORITY_VALUES, 'Select a priority')
    .required('Priority is required'),
  assigned_dashboard_user_ids: array().of(number().integer().positive()),
});

const getClientAdminRemindersQueryKey = (clientId: number | string) => [
  'clientAdminReminders',
  String(clientId),
];

const toDashboardUserOption = (
  user?: DashboardUserSource | null
): DashboardUserOption | null => {
  const id = Number(user?.id);

  if (!Number.isInteger(id) || id <= 0) return null;

  return {
    id,
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    email: user?.email ?? null,
    profile_image: user?.profile_image ?? null,
    job_title: user?.job_title ?? null,
    display_name: user?.display_name ?? null,
  };
};

const getDashboardUserDisplayName = (
  user?: Partial<DashboardUserOption> | null
) => {
  const displayName = user?.display_name?.trim();
  if (displayName) return displayName;

  const fullName = [user?.first_name, user?.last_name]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

  return fullName || user?.email || `Dashboard user #${user?.id ?? ''}`;
};

const getDashboardUserInitials = (
  user?: Partial<DashboardUserOption> | null
) => {
  const name = getDashboardUserDisplayName(user);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return initials || '#';
};

const sortDashboardUsers = (users: DashboardUserOption[]) =>
  [...users].sort((left, right) =>
    getDashboardUserDisplayName(left).localeCompare(
      getDashboardUserDisplayName(right)
    )
  );

const getDaysBeforeLabel = (daysBefore: number) => {
  if (daysBefore === 0) return 'Same day';
  if (daysBefore === 1) return '1 day before';

  return `${daysBefore} days before`;
};

const getPriorityOption = (priority: TaskPriority) =>
  TASK_PRIORITY_OPTIONS.find((option) => option.value === priority) ??
  TASK_PRIORITY_OPTIONS[2];

const getReminderFormValues = (
  reminder?: ClientAdminReminder | null
): ClientAdminReminderFormValues => ({
  title: reminder?.title ?? DEFAULT_REMINDER_FORM_VALUES.title,
  note: reminder?.note ?? DEFAULT_REMINDER_FORM_VALUES.note,
  days_before:
    reminder?.days_before ?? DEFAULT_REMINDER_FORM_VALUES.days_before,
  task_priority:
    reminder?.task_priority ?? DEFAULT_REMINDER_FORM_VALUES.task_priority,
  assigned_dashboard_user_ids: normalizeTaskAssigneeIds(
    reminder?.assigned_dashboard_user_ids
  ),
});

const getReminderPayload = (
  values: ClientAdminReminderFormValues
): ClientAdminReminderDto => {
  const note = values.note.trim();

  return {
    title: values.title.trim(),
    note: note || null,
    days_before: Number(values.days_before),
    task_priority: values.task_priority,
    assigned_dashboard_user_ids: normalizeTaskAssigneeIds(
      values.assigned_dashboard_user_ids
    ),
  };
};

const upsertReminder = (
  reminders: ClientAdminReminder[],
  reminder: ClientAdminReminder
) => {
  const exists = reminders.some((current) => current.id === reminder.id);

  if (!exists) return [reminder, ...reminders];

  return reminders.map((current) =>
    current.id === reminder.id ? reminder : current
  );
};

const getReminderAssignees = (
  reminder: ClientAdminReminder,
  dashboardUsersById: Record<number, DashboardUserOption>
) => {
  const assignedUsersById = new Map<number, DashboardUserOption>();

  reminder.assigned_dashboard_users?.forEach((user) => {
    const option = toDashboardUserOption(user);
    if (option) assignedUsersById.set(option.id, option);
  });

  const assigneeIds = normalizeTaskAssigneeIds(
    reminder.assigned_dashboard_user_ids?.length
      ? reminder.assigned_dashboard_user_ids
      : Array.from(assignedUsersById.keys())
  );

  return assigneeIds.map(
    (id) =>
      assignedUsersById.get(id) ??
      dashboardUsersById[id] ?? {
        id,
        first_name: '',
        last_name: '',
        email: null,
        profile_image: null,
        job_title: null,
        display_name: null,
      }
  );
};

type ClientAdminReminderDialogProps = {
  open: boolean;
  reminder: ClientAdminReminder | null;
  dashboardUsers: DashboardUserOption[];
  isDashboardUsersLoading: boolean;
  isSubmitting: boolean;
  onClose: VoidFunction;
  onSubmit: (payload: ClientAdminReminderDto) => Promise<unknown>;
};

function ClientAdminReminderDialog({
  open,
  reminder,
  dashboardUsers,
  isDashboardUsersLoading,
  isSubmitting,
  onClose,
  onSubmit,
}: ClientAdminReminderDialogProps) {
  const methods = useForm<ClientAdminReminderFormValues>({
    defaultValues: DEFAULT_REMINDER_FORM_VALUES,
    resolver: yupResolver(ADMIN_REMINDER_FORM_SCHEMA),
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting: isFormSubmitting },
  } = methods;

  const dashboardUsersById = useMemo(
    () =>
      dashboardUsers.reduce<Record<number, DashboardUserOption>>(
        (usersById, user) => {
          usersById[user.id] = user;
          return usersById;
        },
        {}
      ),
    [dashboardUsers]
  );

  useEffect(() => {
    if (open) {
      reset(getReminderFormValues(reminder));
    }
  }, [open, reminder, reset]);

  const handleFormSubmit = async (values: ClientAdminReminderFormValues) => {
    await onSubmit(getReminderPayload(values));
  };

  const isSaving = isSubmitting || isFormSubmitting;

  const handleClose = () => {
    if (isSaving) return;

    reset(DEFAULT_REMINDER_FORM_VALUES);
    onClose();
  };

  return (
    <DialogSimple
      dialogTitle={`${reminder ? 'Edit' : 'Add'} admin reminder`}
      open={open}
      onClickButtonClose={handleClose}
      loadingButton={false}
      disagreeButton={false}
      maxWidth="sm"
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={2} sx={{ px: 3, pb: 1 }}>
          <RHFTextField
            name="title"
            label="Title"
            fullWidth
            size="small"
            inputProps={{ maxLength: 255 }}
          />

          <RHFTextField
            name="note"
            label="Note"
            multiline
            minRows={4}
            fullWidth
            size="small"
            inputProps={{ maxLength: 1000 }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <RHFTextField
              name="days_before"
              label="Days before"
              type="number"
              fullWidth
              size="small"
              inputProps={{
                min: 0,
                max: 365,
                step: 1,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
            />

            <RHFTextField
              select
              name="task_priority"
              label="Priority"
              fullWidth
              size="small"
            >
              {TASK_PRIORITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFTextField>
          </Stack>

          <Controller
            name="assigned_dashboard_user_ids"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const selectedUsers = normalizeTaskAssigneeIds(field.value).map(
                (id) =>
                  dashboardUsersById[id] ?? {
                    id,
                    first_name: '',
                    last_name: '',
                    email: null,
                    profile_image: null,
                    job_title: null,
                    display_name: null,
                  }
              );

              return (
                <Autocomplete
                  multiple
                  openOnFocus
                  filterSelectedOptions
                  loading={isDashboardUsersLoading}
                  options={dashboardUsers}
                  value={selectedUsers}
                  onChange={(_event, users) =>
                    field.onChange(
                      normalizeTaskAssigneeIds(users.map((user) => user.id))
                    )
                  }
                  getOptionLabel={getDashboardUserDisplayName}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={option.profile_image ?? undefined}
                          alt={getDashboardUserDisplayName(option)}
                          sx={{ width: 28, height: 28 }}
                        >
                          {getDashboardUserInitials(option)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {getDashboardUserDisplayName(option)}
                          </Typography>
                          {option.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: 'block' }}
                            >
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getDashboardUserDisplayName(option)}
                        avatar={
                          <Avatar
                            src={option.profile_image ?? undefined}
                            alt={getDashboardUserDisplayName(option)}
                          >
                            {getDashboardUserInitials(option)}
                          </Avatar>
                        }
                        sx={{ borderRadius: 0.75 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dashboard assignees"
                      placeholder={selectedUsers.length ? '' : 'Unassigned'}
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              );
            }}
          />
        </Stack>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" disabled={isSaving} onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving}>
            {reminder ? 'Save changes' : 'Create reminder'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </DialogSimple>
  );
}

type ClientAdminReminderTableRowProps = {
  reminder: ClientAdminReminder;
  dashboardUsersById: Record<number, DashboardUserOption>;
  canUpdate: boolean;
  onEdit: (reminder: ClientAdminReminder) => void;
  onDelete: (reminder: ClientAdminReminder) => void;
};

function ClientAdminReminderTableRow({
  reminder,
  dashboardUsersById,
  canUpdate,
  onEdit,
  onDelete,
}: ClientAdminReminderTableRowProps) {
  const { openMenu, handleOpenMenu, handleCloseMenu } = use3DotMenu();
  const priorityOption = getPriorityOption(reminder.task_priority);
  const assignees = getReminderAssignees(reminder, dashboardUsersById);
  const note = reminder.note?.trim();

  const handleEdit = () => {
    onEdit(reminder);
    handleCloseMenu();
  };

  const handleDelete = () => {
    onDelete(reminder);
    handleCloseMenu();
  };

  return (
    <TableRow hover>
      <TableCell align="left">
        <Typography variant="subtitle2" noWrap>
          {reminder.title}
        </Typography>
      </TableCell>

      <TableCell align="left">
        <Typography variant="body2">
          {getDaysBeforeLabel(reminder.days_before)}
        </Typography>
      </TableCell>

      <TableCell align="left">
        <Label color={priorityOption.color}>{priorityOption.label}</Label>
      </TableCell>

      <TableCell align="left">
        {assignees.length ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {assignees.map((assignee) => (
              <Chip
                key={assignee.id}
                size="small"
                label={getDashboardUserDisplayName(assignee)}
                avatar={
                  <Avatar
                    src={assignee.profile_image ?? undefined}
                    alt={getDashboardUserDisplayName(assignee)}
                  >
                    {getDashboardUserInitials(assignee)}
                  </Avatar>
                }
                sx={{ borderRadius: 0.75 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Unassigned
          </Typography>
        )}
      </TableCell>

      <TableCell align="left">
        <Typography
          variant="body2"
          color={note ? 'text.primary' : 'text.secondary'}
          noWrap
          sx={{ maxWidth: 360 }}
        >
          {note || 'No note'}
        </Typography>
      </TableCell>

      <TableCell align="right">
        {canUpdate && (
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            arrow="top-right"
            actions={
              <>
                <MenuItem onClick={handleEdit}>
                  <Iconify icon="eva:edit-outline" />
                  Edit
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                  <Iconify icon="eva:trash-2-outline" />
                  Delete
                </MenuItem>
              </>
            }
          />
        )}
      </TableCell>
    </TableRow>
  );
}

export default function ClientAdminReminders() {
  const { userId } = useParams();
  const clientId = Number(userId);
  const hasClientId = Boolean(userId) && Number.isFinite(clientId);
  const queryClient = useQueryClient();
  const { canUpdate } = useGetAccess('clients');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const remindersQueryKey = getClientAdminRemindersQueryKey(clientId);
  const [editingReminder, setEditingReminder] =
    useState<ClientAdminReminder | null>(null);
  const [deletingReminder, setDeletingReminder] =
    useState<ClientAdminReminder | null>(null);
  const {
    toggle: isReminderDialogOpen,
    onOpen: openReminderDialog,
    onClose: closeReminderDialog,
  } = useToggle();

  const { data: reminders, isLoading: isRemindersLoading } = useQuery({
    queryKey: remindersQueryKey,
    queryFn: () => getClientAdminReminders(clientId),
    enabled: hasClientId,
    onError: (error) => {
      errorMsg(error, 'Unable to load admin reminders');
    },
    refetchOnWindowFocus: false,
  });

  const { data: activeDashboardUsers, isLoading: isDashboardUsersLoading } =
    useQuery({
      queryKey: ['dashboardUsers', 'active'],
      queryFn: getActiveDashboardUsers,
      enabled: hasClientId,
      onError: (error) => {
        errorMsg(error, 'Unable to load dashboard users');
      },
      refetchOnWindowFocus: false,
    });

  const activeReminders = useMemo(
    () => (reminders ?? []).filter((reminder) => reminder.active !== false),
    [reminders]
  );

  const dashboardUserOptions = useMemo(() => {
    const usersById = new Map<number, DashboardUserOption>();

    (activeDashboardUsers ?? []).forEach((user) => {
      const option = toDashboardUserOption(user);
      if (option) usersById.set(option.id, option);
    });

    activeReminders.forEach((reminder) => {
      reminder.assigned_dashboard_users?.forEach((user) => {
        const option = toDashboardUserOption(user);
        if (option && !usersById.has(option.id))
          usersById.set(option.id, option);
      });
    });

    return sortDashboardUsers(Array.from(usersById.values()));
  }, [activeDashboardUsers, activeReminders]);

  const dashboardUsersById = useMemo(
    () =>
      dashboardUserOptions.reduce<Record<number, DashboardUserOption>>(
        (usersById, user) => {
          usersById[user.id] = user;
          return usersById;
        },
        {}
      ),
    [dashboardUserOptions]
  );

  const updateReminderCaches = (
    updater: (current: ClientAdminReminder[]) => ClientAdminReminder[]
  ) => {
    queryClient.setQueryData<ClientAdminReminder[]>(
      remindersQueryKey,
      (current = []) => updater(current)
    );
    queryClient.setQueryData<User | undefined>(
      ['clients', userId],
      (currentClient) =>
        currentClient
          ? {
              ...currentClient,
              admin_reminders: updater(currentClient.admin_reminders ?? []),
            }
          : currentClient
    );
  };

  const refreshReminderState = () => {
    queryClient.invalidateQueries(remindersQueryKey);
    queryClient.invalidateQueries(['clients']);
    queryClient.invalidateQueries(['clients', userId]);
  };

  const createReminderMutation = useMutation({
    mutationFn: (payload: ClientAdminReminderDto) =>
      createClientAdminReminder(clientId, payload),
    onSuccess: (reminder) => {
      updateReminderCaches((current) => upsertReminder(current, reminder));
      refreshReminderState();
      successMsg('Admin reminder created');
      closeReminderDialog();
    },
    onError: (error) => {
      errorMsg(error, 'Unable to create admin reminder');
    },
  });

  const updateReminderMutation = useMutation({
    mutationFn: ({ ruleId, payload }: UpdateReminderVariables) =>
      updateClientAdminReminder(clientId, ruleId, payload),
    onSuccess: (reminder) => {
      updateReminderCaches((current) => upsertReminder(current, reminder));
      refreshReminderState();
      successMsg('Admin reminder updated');
      closeReminderDialog();
      setEditingReminder(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to update admin reminder');
    },
  });

  const deleteReminderMutation = useMutation({
    mutationFn: (ruleId: number) => deleteClientAdminReminder(clientId, ruleId),
    onSuccess: (_result, ruleId) => {
      updateReminderCaches((current) =>
        current.filter((reminder) => reminder.id !== ruleId)
      );
      refreshReminderState();
      successMsg('Admin reminder deleted');
      setDeletingReminder(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to delete admin reminder');
    },
  });

  const handleOpenCreateDialog = () => {
    setEditingReminder(null);
    openReminderDialog();
  };

  const handleOpenEditDialog = (reminder: ClientAdminReminder) => {
    setEditingReminder(reminder);
    openReminderDialog();
  };

  const handleCloseReminderDialog = () => {
    if (createReminderMutation.isLoading || updateReminderMutation.isLoading) {
      return;
    }

    closeReminderDialog();
    setEditingReminder(null);
  };

  const handleSubmitReminder = (payload: ClientAdminReminderDto) => {
    if (editingReminder) {
      return updateReminderMutation.mutateAsync({
        ruleId: editingReminder.id,
        payload,
      });
    }

    return createReminderMutation.mutateAsync(payload);
  };

  const handleConfirmDelete = () => {
    if (!deletingReminder || deleteReminderMutation.isLoading) return;

    deleteReminderMutation.mutate(deletingReminder.id);
  };

  if (!hasClientId) return null;

  if (isRemindersLoading) {
    return <SkeletonLoadingTable />;
  }

  return (
    <>
      <Card>
        <Stack spacing={2.5} sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ flex: 1 }}>
              <SectionTitle title="Admin reminders" />
            </Box>
            {canUpdate && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenCreateDialog}
              >
                Add reminder
              </Button>
            )}
          </Stack>
        </Stack>

        <Divider />

        <Scrollbar>
          <TableContainer sx={{ minWidth: 920, position: 'relative' }}>
            <Table>
              <TableHeadCustom headLabel={CLIENT_ADMIN_REMINDERS_TABLE_HEAD} />
              <TableBody>
                {activeReminders.map((reminder) => (
                  <ClientAdminReminderTableRow
                    key={reminder.id}
                    reminder={reminder}
                    dashboardUsersById={dashboardUsersById}
                    canUpdate={canUpdate}
                    onEdit={handleOpenEditDialog}
                    onDelete={setDeletingReminder}
                  />
                ))}

                <TableNoData
                  isNotFound={!activeReminders.length}
                  title="No admin reminders for this client."
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>

      <ClientAdminReminderDialog
        open={isReminderDialogOpen}
        reminder={editingReminder}
        dashboardUsers={dashboardUserOptions}
        isDashboardUsersLoading={isDashboardUsersLoading}
        isSubmitting={
          createReminderMutation.isLoading || updateReminderMutation.isLoading
        }
        onClose={handleCloseReminderDialog}
        onSubmit={handleSubmitReminder}
      />

      <DialogSimple
        dialogTitle="Delete admin reminder?"
        open={Boolean(deletingReminder)}
        onClickButtonClose={() => setDeletingReminder(null)}
        onClickButtonLoading={handleConfirmDelete}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={deleteReminderMutation.isLoading}
      >
        <Box sx={{ px: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Existing generated task-board reminders will not be deleted.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <span style={{ fontWeight: 600 }}>
              {deletingReminder?.title ?? 'this reminder'}
            </span>
            ?
          </Typography>
        </Box>
      </DialogSimple>
    </>
  );
}
